import React, { useState } from 'react';
import { X, CheckCircle, Printer, AlertCircle, History, Check, ShieldAlert, ArrowRight, Clock, RefreshCw, Edit2, Truck } from 'lucide-react';
import { approveLoadingRequest, approveLoadingRevision, rejectLoadingRevision, rejectLoadingRequest, cancelLoadingRevision } from '../../../../../services/api/warehouseApi';
import { formatWarehouseDate } from '../../../../../utils/dateUtils';

const LoadingRequestProcessingModal = ({ request, isOpen, onClose, onComplete, onRequestRevision, onPrintPdf, onRefreshData, onEditRequest, currentRole = 'WAREHOUSE_PERSON' }) => {
    const [activeTab, setActiveTab] = useState('details'); // 'details', 'history', 'revision'
    const [warehouseRemarks, setWarehouseRemarks] = useState(request?.warehouseRemarks || 'All items prepared and loaded.');

    if (!isOpen || !request) return null;

    const isSalesperson = currentRole === 'SALES_PERSON';
    const isSalesManager = currentRole === 'SALES_MANAGER';
    const isSupplierAdmin = currentRole === 'SUPPLIER_ADMIN' || currentRole === 'ADMIN';
    const isWarehouseStaff = currentRole === 'WAREHOUSE_PERSON' || currentRole === 'WAREHOUSE_STAFF';

    const canRequestRevision = isSalesperson || isSalesManager || isSupplierAdmin;
    const canMarkAsLoaded = isWarehouseStaff || isSupplierAdmin || isSalesManager;

    const isSubmitted = request.status === 'Submitted' || request.status === 'Pending Approval' || request.status === 'Draft';
    const isApproved = request.status === 'Approved' || request.status === 'Approved (Updated)';
    const isRevisionPending = request.status === 'Revision Pending' || !!request.pendingRevision;
    const isCompleted = request.status === 'Completed' || request.status === 'Loaded';
    const isRejected = request.status === 'Rejected';

    const handleApproveRequest = async () => {
        await approveLoadingRequest(request.id, warehouseRemarks, 'Warehouse Staff');
        if (onRefreshData) onRefreshData();
        onClose();
    };

    const handleRejectRequest = async () => {
        await rejectLoadingRequest(request.id, warehouseRemarks, 'Warehouse Staff');
        if (onRefreshData) onRefreshData();
        onClose();
    };

    const handleMarkAsLoaded = () => {
        onComplete(request.id, {
            status: 'Completed',
            warehouseRemarks: warehouseRemarks || 'Items loaded into vehicle successfully.',
            completedBy: 'Warehouse Staff'
        });
        onClose();
    };

    const handleApproveRevision = async () => {
        await approveLoadingRevision(request.id, warehouseRemarks, 'Warehouse Staff');
        if (onRefreshData) onRefreshData();
        onClose();
    };

    const handleRejectRevision = async () => {
        await rejectLoadingRevision(request.id, warehouseRemarks, 'Warehouse Staff');
        if (onRefreshData) onRefreshData();
        onClose();
    };

    const handleCancelRevision = async () => {
        if (window.confirm('Are you sure you want to cancel your revision request?')) {
            await cancelLoadingRevision(request.id, request.salesperson || 'Salesperson');
            if (onRefreshData) onRefreshData();
            onClose();
        }
    };

    const pendingRevision = request.pendingRevision;

    return (
        <div className="fixed inset-0 z-[9999] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 overflow-y-auto">
            <div className="bg-white dark:bg-slate-900 rounded-2xl sm:rounded-3xl border border-slate-200 dark:border-slate-800 shadow-2xl w-full max-w-4xl flex flex-col max-h-[92vh] overflow-hidden my-auto">
                
                {/* POS Header */}
                <div className="p-4 sm:p-5 border-b border-slate-200/80 dark:border-slate-700 flex items-center justify-between bg-white dark:bg-slate-800 shrink-0">
                    <div className="flex items-center gap-3 min-w-0 flex-1 mr-2">
                        <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 flex items-center justify-center font-bold shrink-0">
                            <Truck className="w-5 h-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <h2 className="text-base sm:text-lg font-black text-slate-800 dark:text-white leading-tight flex items-center gap-2 flex-wrap">
                                {request.requestNo}
                                <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold border ${
                                    isRevisionPending ? 'bg-purple-50 text-purple-600 border-purple-200 dark:bg-purple-900/40 dark:text-purple-300' :
                                    isSubmitted ? 'bg-amber-50 text-amber-600 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300' :
                                    isApproved ? 'bg-blue-50 text-blue-600 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300' :
                                    isCompleted ? 'bg-emerald-50 text-emerald-600 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300' :
                                    isRejected ? 'bg-rose-50 text-rose-600 border-rose-200 dark:bg-rose-900/40 dark:text-rose-300' :
                                    'bg-slate-100 text-slate-700 border-slate-200'
                                }`}>
                                    {isSubmitted ? 'Pending Approval' : request.status}
                                </span>
                            </h2>
                            <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2 text-xs text-slate-500 dark:text-slate-400 mt-1">
                                <span className="truncate">
                                    Salesperson: <strong className="font-semibold text-slate-700 dark:text-slate-300">{request.salesperson}</strong>
                                </span>
                                <span className="hidden sm:inline text-slate-300 dark:text-slate-600">|</span>
                                <span className="inline-flex items-center gap-1 font-bold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/40 px-2 py-0.5 rounded-md border border-blue-100 dark:border-blue-800/60 w-fit text-[11px] sm:bg-transparent sm:dark:bg-transparent sm:border-0 sm:p-0 sm:text-xs sm:font-semibold sm:text-slate-700 sm:dark:text-slate-300">
                                    <Truck className="w-3 h-3 text-blue-500 shrink-0" />
                                    <span>Vehicle: {request.vehicle || 'Van 01'}</span>
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                        <button
                            type="button"
                            onClick={() => onPrintPdf(request)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 rounded-lg text-xs font-semibold transition-colors cursor-pointer"
                        >
                            <Printer className="w-4 h-4" /> Print PDF
                        </button>
                        <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-white rounded-full">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Sub-Header Tabs */}
                <div className="px-6 pt-3 bg-slate-50/50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-700 flex items-center gap-4">
                    <button
                        onClick={() => setActiveTab('details')}
                        className={`pb-2.5 text-xs font-bold transition-all border-b-2 ${
                            activeTab === 'details' ? 'border-blue-600 text-blue-600 dark:text-blue-400' : 'border-transparent text-slate-500 hover:text-slate-800'
                        }`}
                    >
                        Details
                    </button>
                    {isRevisionPending && (
                        <button
                            onClick={() => setActiveTab('revision')}
                            className={`pb-2.5 text-xs font-bold transition-all border-b-2 flex items-center gap-1.5 ${
                                activeTab === 'revision' ? 'border-purple-600 text-purple-600 dark:text-purple-400' : 'border-transparent text-purple-600/80 hover:text-purple-600'
                            }`}
                        >
                            <AlertCircle className="w-3.5 h-3.5" /> Compare Revision Changes
                        </button>
                    )}
                    <button
                        onClick={() => setActiveTab('history')}
                        className={`pb-2.5 text-xs font-bold transition-all border-b-2 flex items-center gap-1.5 ${
                            activeTab === 'history' ? 'border-blue-600 text-blue-600 dark:text-blue-400' : 'border-transparent text-slate-500 hover:text-slate-800'
                        }`}
                    >
                        <History className="w-3.5 h-3.5" /> History
                    </button>
                </div>

                {/* Body Content */}
                <div className="p-6 space-y-6 max-h-[65vh] overflow-y-auto">
                    {/* Status Notice Banner for Pending Approval */}
                    {isSubmitted && (
                        <div className="p-4 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-2xl flex items-center gap-3 text-xs text-amber-800 dark:text-amber-300">
                            <Clock className="w-5 h-5 flex-shrink-0 text-amber-600" />
                            <div>
                                <strong className="font-bold block text-sm">Request Pending Approval</strong>
                                {isSalesperson
                                    ? "This loading request is currently pending warehouse approval. You can review or edit quantities if needed before approval."
                                    : "Review requested quantities below and click 'Approve Request' or 'Reject Request'."}
                            </div>
                        </div>
                    )}

                    {activeTab === 'details' && (
                        <div className="space-y-5">
                            {/* Information Banner */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-50 dark:bg-slate-700/30 rounded-2xl border border-slate-100 dark:border-slate-700 text-xs">
                                <div>
                                    <span className="text-slate-400 block font-semibold text-[10px] uppercase">Vehicle</span>
                                    <span className="font-bold text-slate-800 dark:text-white text-sm">{request.vehicle || 'Van 01'}</span>
                                </div>
                                <div>
                                    <span className="text-slate-400 block font-semibold text-[10px] uppercase">Warehouse</span>
                                    <span className="font-bold text-slate-800 dark:text-white">{request.warehouse || 'Main Warehouse'}</span>
                                </div>
                                <div>
                                    <span className="text-slate-400 block font-semibold text-[10px] uppercase">Salesperson</span>
                                    <span className="font-bold text-slate-800 dark:text-white">{request.salesperson}</span>
                                </div>
                                <div>
                                    <span className="text-slate-400 block font-semibold text-[10px] uppercase">Loading Date</span>
                                    <span className="font-bold text-slate-800 dark:text-white">{formatWarehouseDate(request.loadingDate || request.date)}</span>
                                </div>
                                {request.remarks && (
                                    <div className="col-span-2 md:col-span-4 pt-2 border-t border-slate-200 dark:border-slate-600">
                                        <span className="text-slate-400 block font-semibold text-[10px] uppercase">Salesperson Remarks</span>
                                        <span className="italic text-slate-700 dark:text-slate-300">{request.remarks}</span>
                                    </div>
                                )}
                            </div>

                            {/* Requested Items Table */}
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <h3 className="font-bold text-xs uppercase tracking-wider text-slate-500">
                                        {request.status === 'Approved (Updated)' ? 'Items (Updated)' : 'Requested Items'} ({request.items?.length || 0} Products)
                                    </h3>
                                    <span className="text-[10px] text-blue-600 dark:text-blue-400 font-bold sm:hidden flex items-center gap-1 bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 rounded-md border border-blue-100 dark:border-blue-800">
                                        ↔ Scroll table horizontally
                                    </span>
                                </div>
                                <div className="border border-slate-100 dark:border-slate-700 rounded-2xl overflow-x-auto custom-scrollbar-blue pb-2">
                                    <table className="w-full text-left text-xs min-w-[500px]">
                                        <thead className="bg-slate-50 dark:bg-slate-700/50 text-slate-500 font-semibold border-b border-slate-100 dark:border-slate-700">
                                            <tr>
                                                <th className="p-3 pl-4">Product</th>
                                                <th className="p-3 text-center">Suggested Qty</th>
                                                <th className="p-3 text-center">Requested Qty</th>
                                                <th className="p-3">Source</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100 dark:divide-slate-700 font-medium">
                                            {(request.items || []).map((item) => (
                                                <tr key={item.id || item.productId} className="hover:bg-slate-50/50 dark:hover:bg-slate-700/20">
                                                    <td className="p-3 pl-4 font-bold text-slate-800 dark:text-white">{item.productName}</td>
                                                    <td className="p-3 text-center text-slate-500">{item.suggestedQty || 0} {item.unit || 'pcs'}</td>
                                                    <td className="p-3 text-center text-blue-600 dark:text-blue-400 font-bold">{item.requestedQty} {item.unit || 'pcs'}</td>
                                                    <td className="p-3 text-slate-500 text-[11px]">{item.source || 'Pending Orders'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}

                    {(activeTab === 'revision' || (isRevisionPending && activeTab === 'details')) && pendingRevision && (
                        /* COMPARE & REVIEW CHANGES VIEW FOR WAREHOUSE STAFF */
                        <div className="space-y-5">
                            <div className="p-4 bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-800 rounded-2xl flex items-start gap-3 text-xs text-purple-800 dark:text-purple-300">
                                <AlertCircle className="w-5 h-5 flex-shrink-0 text-purple-600 mt-0.5" />
                                <div>
                                    <strong className="font-semibold block mb-0.5">Revision Pending Review</strong>
                                    Salesperson <strong className="font-bold">{pendingRevision.requestedBy || request.salesperson}</strong> requested changes to this approved request.
                                    <div className="mt-1 font-semibold text-purple-900 dark:text-purple-200">
                                        Reason: "{pendingRevision.reason}"
                                    </div>
                                </div>
                            </div>

                            {/* Diff Comparison Table */}
                            <div className="space-y-2">
                                <h3 className="font-bold text-xs uppercase tracking-wider text-slate-500">Requested Changes Comparison</h3>
                                <div className="border border-slate-200 dark:border-slate-700 rounded-2xl overflow-hidden">
                                    <table className="w-full text-left text-xs">
                                        <thead className="bg-slate-100 dark:bg-slate-700 text-slate-600 font-bold border-b border-slate-200 dark:border-slate-600">
                                            <tr>
                                                <th className="p-3 pl-4">Product</th>
                                                <th className="p-3 text-center">Current Qty</th>
                                                <th className="p-3 text-center">Requested Change</th>
                                                <th className="p-3 text-center">Revised Qty</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100 dark:divide-slate-700 font-medium">
                                            {pendingRevision.requestedItems.map((revItem) => {
                                                const origItem = request.items?.find(i => i.productId === revItem.productId);
                                                const currentQty = origItem ? (origItem.requestedQty ?? origItem.warehouseQty ?? 0) : 0;
                                                const revisedQty = Number(revItem.requestedQty) || 0;
                                                const diff = revisedQty - currentQty;

                                                return (
                                                    <tr key={revItem.productId} className="hover:bg-slate-50 dark:hover:bg-slate-700/30">
                                                        <td className="p-3 pl-4 font-bold text-slate-800 dark:text-white">
                                                            {revItem.productName}
                                                            {!origItem && (
                                                                <span className="ml-2 text-[10px] bg-purple-100 text-purple-700 font-bold px-1.5 py-0.5 rounded">New Item</span>
                                                            )}
                                                        </td>
                                                        <td className="p-3 text-center text-slate-500">{origItem ? `${currentQty} pcs` : '-'}</td>
                                                        <td className="p-3 text-center font-bold">
                                                            <span className={`px-2 py-0.5 rounded-full ${
                                                                diff > 0 ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' :
                                                                diff < 0 ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300' :
                                                                'bg-slate-100 text-slate-600'
                                                            }`}>
                                                                {diff > 0 ? `+${diff}` : diff}
                                                            </span>
                                                        </td>
                                                        <td className="p-3 text-center font-bold text-blue-600 dark:text-blue-400 text-sm">
                                                            {revisedQty} pcs
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'history' && (
                        /* AUDIT HISTORY TRAIL MATCHING STEP 5 DIAGRAM */
                        <div className="space-y-4">
                            <h3 className="font-bold text-xs uppercase tracking-wider text-slate-500">Document Revision & Status Audit Trail</h3>
                            <div className="border border-slate-100 dark:border-slate-700 rounded-2xl p-4 space-y-4">
                                {(request.history || [
                                    { version: 1, title: 'Created', timestamp: request.date, user: request.salesperson, notes: 'Created Loading Request' }
                                ]).map((hist, idx) => (
                                    <div key={idx} className="flex items-start gap-3 border-l-2 border-blue-500 pl-4 py-1 relative">
                                        <div className="w-2.5 h-2.5 rounded-full bg-blue-600 absolute -left-[6px] top-2" />
                                        <div className="space-y-0.5">
                                            <div className="flex items-center gap-2 text-xs font-bold text-slate-800 dark:text-white">
                                                <span>Version {hist.version} - {hist.title}</span>
                                                <span className="text-[10px] font-normal text-slate-400">({formatWarehouseDate(hist.timestamp, true)})</span>
                                            </div>
                                            <div className="text-xs text-slate-600 dark:text-slate-300">By: {hist.user}</div>
                                            {hist.notes && <div className="text-xs text-slate-500 italic mt-0.5">{hist.notes}</div>}
                                        </div>
                                    </div>
                                ))}

                                {isRevisionPending && (
                                    <div className="flex items-start gap-3 border-l-2 border-dashed border-slate-300 pl-4 py-1 relative opacity-60">
                                        <div className="w-2.5 h-2.5 rounded-full bg-slate-300 absolute -left-[6px] top-2" />
                                        <div className="text-xs font-bold text-slate-500">
                                            Awaiting Warehouse Approval
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Warehouse Staff Remarks Field */}
                    <div className="space-y-2 pt-2 border-t border-slate-100 dark:border-slate-700">
                        <label className="block text-xs font-bold text-slate-700 dark:text-slate-300">
                            Warehouse Staff Remarks (Optional)
                        </label>
                        <textarea
                            rows="2"
                            value={warehouseRemarks}
                            disabled={isCompleted || isSalesperson}
                            onChange={(e) => setWarehouseRemarks(e.target.value)}
                            placeholder={isSalesperson ? "Remarks added by warehouse staff" : "Add notes for warehouse verification or loading..."}
                            className="w-full p-3 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-xl text-xs text-slate-800 dark:text-white placeholder:text-slate-400 focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
                        ></textarea>
                    </div>
                </div>

                {/* Footer Action Buttons */}
                <div className="p-3 sm:p-4 px-4 sm:px-6 bg-white dark:bg-slate-800 border-t border-slate-100 dark:border-slate-700 flex items-center justify-between shadow-lg shrink-0 sticky bottom-0 z-20 pb-safe pb-3 sm:pb-4">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-xs font-semibold text-slate-600 dark:text-slate-400 hover:text-slate-800"
                    >
                        Close
                    </button>

                    <div className="flex items-center gap-3">
                        {isSubmitted ? (
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => {
                                        onClose();
                                        if (onEditRequest) onEditRequest(request);
                                    }}
                                    className="px-4 py-2.5 bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 hover:bg-amber-100 rounded-xl text-xs font-bold border border-amber-200 dark:border-amber-800 flex items-center gap-1.5 cursor-pointer"
                                >
                                    <Edit2 className="w-4 h-4" /> Edit Request
                                </button>
                                {isSalesperson ? (
                                    <span className="text-xs font-bold text-amber-600 bg-amber-50 dark:bg-amber-900/30 px-3 py-1.5 rounded-xl border border-amber-200 dark:border-amber-800 flex items-center gap-1.5">
                                        <Clock className="w-4 h-4" /> Pending Warehouse Approval
                                    </span>
                                ) : (
                                    <>
                                        <button
                                            type="button"
                                            onClick={handleRejectRequest}
                                            className="px-4 py-2.5 bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-300 hover:bg-rose-100 rounded-xl text-xs font-bold border border-rose-200 dark:border-rose-800 cursor-pointer"
                                        >
                                            Reject Request
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleApproveRequest}
                                            className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold shadow-md shadow-blue-500/20 flex items-center gap-1.5 cursor-pointer"
                                        >
                                            <Check className="w-4 h-4" /> Approve Request
                                        </button>
                                    </>
                                )}
                            </div>
                        ) : isRevisionPending ? (
                            isSalesperson ? (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => setActiveTab(activeTab === 'revision' ? 'details' : 'revision')}
                                        className="px-3.5 py-2 border border-blue-200 dark:border-blue-800 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded-xl text-xs font-bold cursor-pointer"
                                    >
                                        {activeTab === 'revision' ? 'View Request' : 'View Revision'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleCancelRevision}
                                        className="px-4 py-2 bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 hover:bg-rose-100 rounded-xl text-xs font-bold border border-rose-200 dark:border-rose-800 cursor-pointer"
                                    >
                                        Cancel Revision
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        onClick={handleRejectRevision}
                                        className="px-4 py-2.5 bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-300 hover:bg-rose-100 rounded-xl text-xs font-bold border border-rose-200 dark:border-rose-800 cursor-pointer"
                                    >
                                        Reject Changes
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleApproveRevision}
                                        className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold shadow-md shadow-emerald-500/20 flex items-center gap-1.5 cursor-pointer"
                                    >
                                        <Check className="w-4 h-4" /> Approve Changes
                                    </button>
                                </>
                            )
                        ) : isApproved ? (
                            <>
                                {canRequestRevision && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            onClose();
                                            if (onRequestRevision) onRequestRevision(request);
                                        }}
                                        className="px-4 py-2.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 rounded-xl text-xs font-bold border border-blue-200 dark:border-blue-800 flex items-center gap-1.5 cursor-pointer"
                                    >
                                        <RefreshCw className="w-4 h-4" /> Request Revision
                                    </button>
                                )}
                                {canMarkAsLoaded && (
                                    <button
                                        type="button"
                                        onClick={handleMarkAsLoaded}
                                        className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold shadow-md shadow-emerald-500/20 flex items-center gap-1.5 cursor-pointer"
                                    >
                                        <CheckCircle className="w-4 h-4" /> Mark as Loaded
                                    </button>
                                )}
                            </>
                        ) : isRejected ? (
                            <span className="text-xs text-rose-600 font-bold flex items-center gap-1">
                                <X className="w-4 h-4" /> Request Rejected
                            </span>
                        ) : (
                            <span className="text-xs text-emerald-600 font-bold flex items-center gap-1">
                                <CheckCircle className="w-4 h-4" /> Loaded / Completed
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoadingRequestProcessingModal;


